import {
  GOOGLE_SCOPE_GROUPS,
  GoogleScopeGroup,
  MessagingPlatform,
} from '@this+that/models';

// Two workspace listings in flight at a time. Firing every workspace at once is what turned
// a 4-invocation baseline into 67 and got the listing rate-limited.
export const SLACK_WORKSPACE_FETCH_CONCURRENCY = 2;

export const INTEGRATION_QUERY_PARAMS = {
  AUTO_CONNECT: 'autoConnect',
  FROM_PROMPT: 'fromPrompt',
  // The capability-gap card's deep link back to the Integration page. Its value is only
  // ever an OFFICIAL_MCPS key — see useConnectMcpDeepLink.
  CONNECT_MCP: 'connectMcp',
} as const;

export const INTEGRATION_AUTO_CONNECT_VALUES = {
  GMAIL: 'gmail',
  OUTLOOK: 'outlook',
} as const;

export type IntegrationAutoConnectValue =
  (typeof INTEGRATION_AUTO_CONNECT_VALUES)[keyof typeof INTEGRATION_AUTO_CONNECT_VALUES];

export const USER_METADATA_KEYS = {
  ONBOARDING_COMPLETE: 'ht_onboarding_complete',
  FIRST_ACCESS_MODAL_VIEWED: 'ht_firstAccessModalViewed',
  POST_ONBOARDING_ANALYZE_SHOWN: 'ht_post_onboarding_analyze_shown',
  DOBOX_FIRST_RUN_BANNER_DISMISSED: 'ht_dobox_first_run_banner_dismissed',
  DOBOX_FIRST_RUN_BANNER_ELIGIBLE: 'ht_dobox_first_run_banner_eligible',
  CONTACTS_FIRST_RUN_BANNER_DISMISSED: 'ht_contacts_first_run_banner_dismissed',
} as const;

export const SESSION_STORAGE_KEYS = {
  INTEGRATION_FROM_PROMPT: 'integration_from_prompt',
  POST_OAUTH_RETURN: 'post_oauth_return',
  PROVIDER_CONNECT_PENDING: 'provider_connect_pending',
  PENDING_INTEGRATION_REFRESH: 'pending_integration_refresh',
  CONNECTING_PLATFORM: 'connecting_platform',
  HOSTED_PANEL_OPEN_REQUEST: 'hosted_panel_open_request',
  EXECUTOR_RESUME_MARKER: 'executor_resume_marker',
} as const;

export const NAVIGATION_TYPE_BACK_FORWARD = 'back_forward';

export const PERFORMANCE_ENTRY_TYPE_NAVIGATION = 'navigation';

export const POST_OAUTH_RETURN_TTL_MS = 10 * 60 * 1000;

// ext_final §Retomar sem redigitar — a marker armed just before a capability-gap
// redirect so the DoBox panel can auto-resume on return, without the server-side
// RESUME_AFTER_CONNECT ticket (API-CAP.03) ever reaching the browser.
export const EXECUTOR_RESUME_MARKER_TTL_MS = 30 * 60 * 1000;

// The purge queue only announces the START of a mailbox delete (see
// CommunicationPurgeQueue/utils/processRecord.ts) — there is no "purge done"
// frame — so a removed account is blocked for a fixed window rather than
// until a signal that never arrives.
export const REMOVED_EMAIL_ACCOUNT_TTL_MS = 6 * 60 * 60 * 1000;

export const PRODUCTION_HOSTNAME = 'assistant.thisandthat.chat';

/**
 * Platforms whose card is withheld on the production host — none of them has
 * production clearance yet.
 *
 * Google Business Profile is here for a second reason on top of being unfinished: the GBP
 * APIs sit at 0 QPM until Google approves the project's access request, so every connect
 * resolves zero accounts and the card reads connected with nothing behind it.
 */
export const PRODUCTION_HIDDEN_PLATFORMS: ReadonlySet<MessagingPlatform> =
  new Set([
    MessagingPlatform.WHATSAPP,
    MessagingPlatform.INSTAGRAM,
    MessagingPlatform.MESSENGER,
    MessagingPlatform.GOOGLE_BUSINESS,
  ]);

/**
 * Every card that grants a Google scope group. EXHAUSTIVE over the scope groups, not over
 * MessagingPlatform: `satisfies Record<GoogleScopeGroup, …>` inverted makes a new scope
 * group a compile error here, which is what stops the derivation below from silently
 * yielding nothing for a card nobody mapped.
 *
 * Drive has no card of its own — it is a `buildSpecialPersonalItem` with the string id
 * `'google-drive'`, not a MessagingPlatform — so it is named with `null` rather than left
 * out, because "no card" and "forgot to map it" must not look the same.
 */
const GOOGLE_CARD_BY_SCOPE_GROUP = {
  [GOOGLE_SCOPE_GROUPS.GMAIL]: MessagingPlatform.GOOGLE,
  [GOOGLE_SCOPE_GROUPS.CALENDAR]: MessagingPlatform.GOOGLE_CALENDAR,
  [GOOGLE_SCOPE_GROUPS.CHAT]: MessagingPlatform.GOOGLE_CHAT,
  [GOOGLE_SCOPE_GROUPS.BUSINESS_PROFILE]: MessagingPlatform.GOOGLE_BUSINESS,
  [GOOGLE_SCOPE_GROUPS.DRIVE]: null,
} satisfies Record<GoogleScopeGroup, MessagingPlatform | null>;

/**
 * The scope groups behind the withheld cards, DERIVED — withholding a Google card and
 * withholding its scope group are one decision, and maintaining two lists of it is how the
 * second gets forgotten.
 *
 * It needs both: hiding a card stops new connects from the catalogue, while the restore
 * offer re-requests whatever the snapshot recorded, so an unfiltered offer walks a withdrawn
 * surface back in through a single consent screen the user never chose it on.
 */
export const PRODUCTION_HIDDEN_GOOGLE_SCOPE_GROUPS: ReadonlySet<GoogleScopeGroup> =
  new Set(
    (Object.keys(GOOGLE_CARD_BY_SCOPE_GROUP) as GoogleScopeGroup[]).filter(
      (group) => {
        const card = GOOGLE_CARD_BY_SCOPE_GROUP[group];
        return !!card && PRODUCTION_HIDDEN_PLATFORMS.has(card);
      },
    ),
  );

export function isProductionHost(hostname: string): boolean {
  return hostname === PRODUCTION_HOSTNAME;
}
